import React from 'react';
import { createBrowserRouter, RouterProvider, useLocation, useRouteLoaderData } from 'react-router-dom';
import {
  CustomerVerificationStatus,
  NameMismatchDetails,
  RejectionReason,
} from './api/tvs/customer/model/CustomerVerificationStatus';
import UnexpectedErrorPage from './pages/errorPage/UnexpectedErrorPage';
import KybLandingPage from './pages/kybLandingPage/KybLandingPage';
import NotFoundPage from './pages/notFoundPage/NotFoundPage';
import VerificationLandingPage, {
  loader as verificationLandingPageLoader,
  shouldRevalidate as verificationLandingPageShouldRevalidate,
} from './pages/verificationLandingPage/VerificationLandingPage';
import { DSA_DOCUMENT_CHECK_FAILED_ROUTE_PATH, DSA_VERIFICATION_BASE_PATH } from './routePaths';
import DsaNotificationPage from './pages/dsaNotificationPage/DsaNotificationPage';
import DsaPendingPage from './pages/dsaPendingPage/DsaPendingPage';

interface KybLandingPageLocationState {
  rejectionReason?: RejectionReason | null;
  nameMismatch?: NameMismatchDetails | null;
}

const DocumentCheckFailedPage: React.FC = () => {
  const location = useLocation();
  const verificationStatus = useRouteLoaderData('kyb-verification-root') as CustomerVerificationStatus | undefined;
  const locationState = location.state as KybLandingPageLocationState | null;
  const hasNavigatedReason = !!locationState?.rejectionReason;
  const errorReason = hasNavigatedReason ? locationState?.rejectionReason : verificationStatus?.rejectionReason;
  const nameMismatch = hasNavigatedReason ? locationState?.nameMismatch : verificationStatus?.nameMismatch;

  return <KybLandingPage documentCheckFailed errorReason={errorReason} nameMismatch={nameMismatch} />;
};

const router = [
  {
    path: DSA_VERIFICATION_BASE_PATH,
    loader: verificationLandingPageLoader,
    shouldRevalidate: verificationLandingPageShouldRevalidate,
    element: <VerificationLandingPage />,
    errorElement: <UnexpectedErrorPage />,
    id: 'kyb-verification-root',
    children: [
      {
        index: true,
        element: <KybLandingPage />,
      },
      {
        path: DSA_DOCUMENT_CHECK_FAILED_ROUTE_PATH,
        element: <DocumentCheckFailedPage />,
      },
      {
        path: 'document-uploading',
        element: <DsaPendingPage />,
      },
      {
        path: 'documents-in-review',
        element: (
          <DsaNotificationPage
            title='In Prüfung'
            description='Eine manuelle Prüfung ist erforderlich. Die Bearbeitung kann länger dauern. Sie können den Browser jetzt schließen.'
            checkCustomerStatus
          />
        ),
      },
      {
        path: 'documents-reviewed',
        element: <DsaNotificationPage title='Verifiziert' description='Ihr Dokument wurde erfolgreich verifiziert.' />,
      },
      {
        path: 'error',
        element: <NotFoundPage />,
      },
    ],
  },
  {
    path: '*',
    element: <NotFoundPage />,
    errorElement: <UnexpectedErrorPage />,
  },
];

const browserRouter = createBrowserRouter(router);

const Root: React.FC = () => {
  return <RouterProvider router={browserRouter} />;
};

export default Root;
